(() => {
  const formatMoney = (amount, currency = 'GBP', locale = document.documentElement.lang || 'en') => {
    const cents = Number(amount);
    if (!Number.isFinite(cents)) return '';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const parseDeadline = (value, mode = 'fixed') => {
    if (!value) return null;

    if (mode === 'visitor') {
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
      if (!match) return null;
      const [, year, month, day, hour, minute, second = '0'] = match;
      const timestamp = new Date(
        Number(year),
        Number(month) - 1,
        Number(day),
        Number(hour),
        Number(minute),
        Number(second)
      ).getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    }

    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  };

  window.WarmouraProduct = { formatMoney, parseDeadline };

  if (!customElements.get('warmoura-product-shell')) {
    customElements.define(
      'warmoura-product-shell',
      class WarmouraProductShell extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.saleBadge = this.querySelector('[data-warmoura-sale-badge]');
          this.savingBadge = this.querySelector('[data-warmoura-saving]');
          this.submitButton = this.querySelector('[id^="ProductSubmitButton-"]');

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (data?.sectionId && data.sectionId !== this.dataset.sectionId) return;
              const variant = data?.variant;
              if (this.saleBadge && variant) {
                this.saleBadge.classList.toggle(
                  'hidden',
                  !(Number(variant.compare_at_price) > Number(variant.price))
                );
              }
              this.updateSaving(variant);
            });
            this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source } = {}) => {
              if (source === 'product-form') this.showSubmitSuccess();
            });
          }
        }

        disconnectedCallback() {
          this.variantUnsubscriber?.();
          this.cartUnsubscriber?.();
          window.clearTimeout(this.successTimer);
          this.initialized = false;
        }

        updateSaving(variant) {
          if (!this.savingBadge || !variant) return;
          const compare = Number(variant.compare_at_price);
          const price = Number(variant.price);
          if (!Number.isFinite(compare) || !Number.isFinite(price) || compare <= price) {
            this.savingBadge.hidden = true;
            this.savingBadge.textContent = '';
            return;
          }
          const percent = Math.round(((compare - price) / compare) * 100);
          const label = this.savingBadge.dataset.savingLabel || 'Save';
          this.savingBadge.textContent = `${label} ${percent}%`;
          this.savingBadge.hidden = percent <= 0;
        }

        showSubmitSuccess() {
          const label = this.submitButton?.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
          if (!label || this.submitButton.disabled) return;
          const original = label.textContent;
          const sticky = this.querySelector('warmoura-sticky-purchase');
          label.textContent = sticky?.dataset.successLabel || 'Added';
          this.submitButton.classList.add('is-success');
          window.clearTimeout(this.successTimer);
          this.successTimer = window.setTimeout(() => {
            this.submitButton.classList.remove('is-success');
            label.textContent = original;
          }, 1800);
        }
      }
    );
  }

  if (!customElements.get('warmoura-sticky-purchase')) {
    customElements.define(
      'warmoura-sticky-purchase',
      class WarmouraStickyPurchase extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.mainSubmit = document.querySelector(this.dataset.mainSubmit);
          if (!this.mainSubmit) return;

          this.button = this.querySelector('[data-sticky-submit]');
          this.buttonText = this.button?.querySelector('[data-sticky-submit-text]');
          this.price = this.querySelector('[data-sticky-price]');
          this.label = this.querySelector('[data-sticky-label]');
          this.image = this.querySelector('[data-sticky-image]');
          this.mainPrice = document.querySelector(this.dataset.mainPrice);
          this.mainGallery = document.querySelector(this.dataset.mainGallery);
          this.handleButtonClick = this.submitMainForm.bind(this);
          this.handleViewportChange = this.queueVisibilityUpdate.bind(this);
          this.button?.addEventListener('click', this.handleButtonClick);
          window.addEventListener('scroll', this.handleViewportChange, { passive: true });
          window.addEventListener('resize', this.handleViewportChange, { passive: true });

          this.stateObserver = new MutationObserver(() => this.syncState());
          this.stateObserver.observe(this.mainSubmit, {
            attributes: true,
            childList: true,
            subtree: true,
          });

          this.cartSurfaces = Array.from(document.querySelectorAll('cart-drawer, cart-notification'));
          this.cartObserver = new MutationObserver(() => this.updateVisibility());
          this.cartSurfaces.forEach((surface) => this.cartObserver.observe(surface, { attributes: true }));

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (data?.sectionId && data.sectionId !== this.dataset.sectionId) return;
              window.requestAnimationFrame(() => this.syncState(true));
            });
            this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source } = {}) => {
              if (source !== 'product-form') return;
              this.showSuccess();
            });
          }

          this.syncState(true);
          this.updateVisibility();
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.handleButtonClick);
          window.removeEventListener('scroll', this.handleViewportChange);
          window.removeEventListener('resize', this.handleViewportChange);
          this.stateObserver?.disconnect();
          this.cartObserver?.disconnect();
          this.variantUnsubscriber?.();
          this.cartUnsubscriber?.();
          window.clearTimeout(this.successTimer);
          document.body.classList.remove('warmoura-sticky-purchase-visible');
          this.initialized = false;
        }

        submitMainForm() {
          if (!this.mainSubmit || this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true') return;
          this.mainSubmit.click();
        }

        syncState(refreshMedia = false) {
          if (!this.mainSubmit || !this.button) return;
          const disabled = this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true';
          this.button.disabled = disabled;
          this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
          this.classList.toggle('is-busy', this.mainSubmit.classList.contains('loading'));
          const sourceText = this.mainSubmit.querySelector('span:not(.sold-out-message):not(.loading__spinner)')?.textContent?.trim();
          if (sourceText && !this.classList.contains('is-success') && this.buttonText) this.buttonText.textContent = sourceText;
          if (this.mainPrice && this.price) this.price.innerHTML = this.mainPrice.innerHTML;

          const linkedColour = document.querySelector(
            '[data-linked-colour-products] [aria-current="page"] [data-linked-colour-label]'
          );
          const selectedColour = document.querySelector('[data-native-colour-option] input:checked');
          if (this.label && linkedColour) this.label.textContent = linkedColour.textContent.trim();
          else if (this.label && selectedColour) this.label.textContent = selectedColour.value;

          if (refreshMedia && this.image && this.mainGallery) {
            const activeImage = this.mainGallery.querySelector('.is-active img') || this.mainGallery.querySelector('img');
            if (activeImage?.currentSrc || activeImage?.src) {
              this.image.src = activeImage.currentSrc || activeImage.src;
              this.image.alt = activeImage.alt || this.image.alt;
            }
          }
          this.updateVisibility();
        }

        showSuccess() {
          if (!this.buttonText) return;
          this.classList.add('is-success');
          this.buttonText.textContent = this.dataset.successLabel || 'Added';
          window.clearTimeout(this.successTimer);
          this.successTimer = window.setTimeout(() => {
            this.classList.remove('is-success');
            this.syncState();
          }, 1800);
        }

        queueVisibilityUpdate() {
          if (this.visibilityFrame) return;
          this.visibilityFrame = window.requestAnimationFrame(() => {
            this.visibilityFrame = null;
            this.updateVisibility();
          });
        }

        updateVisibility() {
          if (!this.mainSubmit) return;
          const isMobile = window.matchMedia('(max-width: 989px)').matches;
          const enabled = isMobile ? this.dataset.mobile === 'true' : this.dataset.desktop === 'true';
          const passedMainButton = this.mainSubmit.getBoundingClientRect().bottom < 0;
          const cartOpen = this.cartSurfaces?.some(
            (surface) => surface.classList.contains('active') || surface.hasAttribute('open')
          );
          const visible = enabled && passedMainButton && !cartOpen;
          this.classList.toggle('is-visible', visible);
          this.setAttribute('aria-hidden', visible ? 'false' : 'true');
          document.body.classList.toggle('warmoura-sticky-purchase-visible', visible && isMobile);
        }
      }
    );
  }

  if (!customElements.get('warmoura-carousel')) {
    customElements.define(
      'warmoura-carousel',
      class WarmouraCarousel extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.track = this.querySelector('[data-carousel-track]');
          if (!this.track) return;
          this.previous = this.querySelector('[data-carousel-previous]');
          this.next = this.querySelector('[data-carousel-next]');
          this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
          this.cards = Array.from(this.querySelectorAll('[data-carousel-card]'));
          this.activeIndex = 0;
          this.handlePrevious = () => this.move(-1);
          this.handleNext = () => this.move(1);
          this.handleScroll = () => this.queueUpdate();
          this.handleKeydown = (event) => this.onKeydown(event);
          this.previous?.addEventListener('click', this.handlePrevious);
          this.next?.addEventListener('click', this.handleNext);
          // Passive: the track is a native scroller and nothing here cancels
          // the gesture. Scroll position only ever gets read.
          this.track.addEventListener('scroll', this.handleScroll, { passive: true });
          this.track.addEventListener('keydown', this.handleKeydown);
          this.dotHandlers = this.dots.map((dot, index) => {
            const handler = () => this.goTo(index);
            dot.addEventListener('click', handler);
            return handler;
          });
          this.observeCards();
          this.measure();
          this.update();
        }

        disconnectedCallback() {
          this.previous?.removeEventListener('click', this.handlePrevious);
          this.next?.removeEventListener('click', this.handleNext);
          this.track?.removeEventListener('scroll', this.handleScroll);
          this.track?.removeEventListener('keydown', this.handleKeydown);
          this.dots?.forEach((dot, index) => dot.removeEventListener('click', this.dotHandlers[index]));
          this.observer?.disconnect();
          this.observer = null;
          if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
          this.updateFrame = null;
          // Cleared so a Theme Editor section reload rebuilds cleanly instead of
          // returning early and leaving a dead element behind.
          this.initialized = false;
        }

        // The active card is whichever is most visible in the scrollport. Read
        // this way rather than from scrollLeft/step, it stays correct after a
        // native swipe, with scroll padding, and at any card width.
        observeCards() {
          if (!('IntersectionObserver' in window) || !this.cards.length) return;
          this.ratios = new Map();
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => this.ratios.set(entry.target, entry.intersectionRatio));
              let best = 0;
              let bestRatio = -1;
              this.cards.forEach((card, index) => {
                const ratio = this.ratios.get(card) ?? 0;
                if (ratio > bestRatio + 0.01) {
                  bestRatio = ratio;
                  best = index;
                }
              });
              this.activeIndex = best;
              // A card only carries its text once it is essentially whole in
              // the scrollport. The sliver of a neighbour at the viewport edge
              // therefore never shows a few clipped letters of its heading.
              this.cards.forEach((card) => {
                card.classList.toggle('is-partial', (this.ratios.get(card) ?? 0) < 0.9);
              });
              this.paintDots();
            },
            { root: this.track, threshold: [0, 0.25, 0.5, 0.75, 0.9, 0.99] }
          );
          this.cards.forEach((card) => this.observer.observe(card));
        }

        onKeydown(event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          this.move(event.key === 'ArrowRight' ? 1 : -1);
        }

        measure() {
          const style = window.getComputedStyle?.(this.track);
          this.gap = Number.parseFloat(style?.columnGap || style?.gap || '0') || 0;
        }

        getStep() {
          const card = this.track?.querySelector('[data-carousel-card]');
          return card ? card.getBoundingClientRect().width + (this.gap || 0) : 0;
        }

        move(direction) {
          const distance = this.getStep() * Number(direction || 0);
          if (!distance) return;
          const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          this.track.scrollBy({ left: distance, behavior: reducedMotion ? 'auto' : 'smooth' });
        }

        // Scrolled to the card's own offset rather than a multiple of the step,
        // so scroll padding and any rounding can never land between snap points.
        goTo(index) {
          const card = this.cards?.[index];
          const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
          const behavior = reducedMotion ? 'auto' : 'smooth';
          if (card) {
            const padding = Number.parseFloat(window.getComputedStyle(this.track).scrollPaddingLeft) || 0;
            const left = card.offsetLeft - this.track.offsetLeft - padding;
            this.track.scrollTo({ left: Math.max(0, left), behavior });
            return;
          }
          const step = this.getStep();
          if (!step) return;
          this.track.scrollTo({ left: step * index, behavior });
        }

        queueUpdate() {
          if (this.updateFrame) return;
          this.updateFrame = window.requestAnimationFrame(() => {
            this.updateFrame = null;
            this.update();
          });
        }

        update() {
          const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
          if (this.previous) this.previous.disabled = this.track.scrollLeft <= 2;
          if (this.next) this.next.disabled = this.track.scrollLeft >= maxScroll;
          // Without IntersectionObserver, fall back to the step estimate.
          if (!this.observer) {
            const step = this.getStep();
            if (step) this.activeIndex = Math.max(0, Math.round(this.track.scrollLeft / step));
          }
          this.paintDots();
        }

        paintDots() {
          this.dots?.forEach((dot, dotIndex) => {
            if (dotIndex === this.activeIndex) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
          // Marks the card the shopper is actually on. On mobile the stylesheet
          // uses this to hold back the neighbouring card's title, so the sliver
          // of the next card never shows a few clipped letters of its heading.
          this.cards?.forEach((card, cardIndex) => {
            card.classList.toggle('is-active', cardIndex === this.activeIndex);
          });
        }
      }
    );
  }

  if (!customElements.get('warmoura-countdown')) {
    customElements.define(
      'warmoura-countdown',
      class WarmouraCountdown extends HTMLElement {
        connectedCallback() {
          if (this.initialized) return;
          this.initialized = true;
          this.output = this.querySelector('[data-countdown-output]');
          this.deadline = parseDeadline(this.dataset.endAt, this.dataset.timezoneMode);
          if (!this.deadline || !this.output) {
            this.handleExpired();
            return;
          }
          this.tick();
          this.timer = window.setInterval(() => this.tick(), 1000);
        }

        disconnectedCallback() {
          window.clearInterval(this.timer);
          this.initialized = false;
        }

        tick() {
          const remaining = this.deadline - Date.now();
          if (remaining <= 0) {
            this.handleExpired();
            return;
          }
          const totalSeconds = Math.floor(remaining / 1000);
          const values = {
            days: Math.floor(totalSeconds / 86400),
            hours: Math.floor((totalSeconds % 86400) / 3600),
            minutes: Math.floor((totalSeconds % 3600) / 60),
            seconds: totalSeconds % 60,
          };
          Object.entries(values).forEach(([unit, value]) => {
            const target = this.output.querySelector(`[data-countdown-${unit}]`);
            if (target) target.textContent = String(value).padStart(2, '0');
          });
          this.output.setAttribute(
            'aria-label',
            `${values.days} days, ${values.hours} hours, ${values.minutes} minutes, ${values.seconds} seconds remaining`
          );
        }

        handleExpired() {
          window.clearInterval(this.timer);
          if (this.dataset.hideWhenExpired === 'true') {
            this.hidden = true;
            return;
          }
          if (this.output) this.output.textContent = this.dataset.expiredText || 'Offer ended';
        }
      }
    );
  }
})();

/* ==========================================================================
   Three-tier bundle selector
   Buy 1 / Duo / Trio. The chosen tier fixes the number of configuration rows,
   so there is no add or remove flow. Each row picks a genuine product and a
   genuine variant, and every row becomes its own cart line via
   /cart/add.js items[].

   Percentages mirror the live Shopify automatic discounts. The saving is
   allocated per line and floored per line, exactly as Shopify allocates it,
   so the figure on the page equals the cart and checkout to the penny.
   ========================================================================== */
(() => {
  const money = (cents, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2 }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const readJSON = (host, selector, fallback) => {
    try {
      const raw = host.querySelector(selector)?.textContent;
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  };

  if (customElements.get('warmoura-bundle-builder')) return;

  customElements.define(
    'warmoura-bundle-builder',
    class WarmouraBundleBuilder extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.catalogue = readJSON(this, '[data-bundle-catalogue]', []);
        if (!this.catalogue.length) return;
        this.defaults = readJSON(this, '[data-bundle-defaults]', { roles: {}, tiers: {} });

        this.currency = this.dataset.currency || 'GBP';
        this.locale = document.documentElement.lang || 'en';
        this.errorEl = this.querySelector('[data-bundle-error]');
        this.form = document.getElementById(this.dataset.formId);
        this.submitting = false;
        this.submitButton = this.form?.querySelector('[type="submit"]');
        this.submitLabel = this.submitButton?.querySelector(
          'span:not(.sold-out-message):not(.loading__spinner)'
        );

        this.tiers = Array.from(this.querySelectorAll('[data-tier-option]')).map((option) => ({
          option,
          tier: Number(option.dataset.tier) || 1,
          percent: Number(option.dataset.tierPercent) || 0,
          input: option.querySelector('[data-tier-input]'),
          rowsHost: option.querySelector('[data-tier-rows]'),
          rows: Array.from(option.querySelectorAll('[data-bundle-row]')),
        }));
        if (!this.tiers.length) return;

        this.applyDefaults();
        this.tiers.forEach((entry) => entry.rows.forEach((row) => this.initRow(row)));

        this.handleTierChange = () => this.selectTier();
        this.tiers.forEach((entry) => entry.input?.addEventListener('change', this.handleTierChange));

        this.handleSubmit = this.handleSubmit.bind(this);
        document.addEventListener('submit', this.handleSubmit, true);

        this.selectTier(false);
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.handleSubmit, true);
        this.tiers?.forEach((entry) => entry.input?.removeEventListener('change', this.handleTierChange));
        window.clearTimeout(this.successTimer);
        this.initialized = false;
      }

      // ---- defaults --------------------------------------------------------

      // A slot stores a role rather than a product id, so one set of Theme
      // Editor settings gives the Period page "Pants + Robe" and the Robe page
      // "Robe + Pro" without ever putting Period controls in a Robe row.
      resolveRole(role) {
        const roles = this.defaults?.roles || {};
        const id = Number(roles[role]);
        if (Number.isFinite(id) && this.catalogue.some((p) => p.id === id)) return id;
        const current = Number(roles.current);
        if (Number.isFinite(current) && this.catalogue.some((p) => p.id === current)) return current;
        return this.catalogue[0].id;
      }

      applyDefaults() {
        const map = this.defaults?.tiers || {};
        this.tiers.forEach((entry) => {
          const roles = map[String(entry.tier)] || [];
          entry.rows.forEach((row, index) => {
            const select = row.querySelector('[data-row-product]');
            if (!select) return;
            const wanted = String(this.resolveRole(roles[index]));
            if (Array.from(select.options).some((opt) => opt.value === wanted)) select.value = wanted;
          });
        });
      }

      // ---- tier selection --------------------------------------------------

      selectedTier() {
        return this.tiers.find((entry) => entry.input?.checked) || this.tiers[0];
      }

      selectTier(announce = true) {
        const active = this.selectedTier();
        this.tiers.forEach((entry) => {
          const isActive = entry === active;
          entry.option.classList.toggle('is-selected', isActive);
          if (entry.rowsHost) entry.rowsHost.hidden = !isActive;
        });
        this.recalculate();
        if (announce) this.setError('');
      }

      // ---- rows ------------------------------------------------------------

      initRow(row) {
        const productSelect = row.querySelector('[data-row-product]');
        this.buildRow(row);
        productSelect?.addEventListener('change', () => {
          this.buildRow(row);
          this.recalculate();
        });
      }

      productFor(row) {
        // Tolerates a missing catalogue rather than throwing: the row still has
        // a resolved variant to add to the cart, and only the thumbnail that
        // travels with it would be lost.
        const catalogue = this.catalogue || [];
        const id = Number(row.querySelector('[data-row-product]')?.value);
        return catalogue.find((p) => p.id === id) || catalogue[0];
      }

      // ---- the three questions a row asks ----------------------------------
      // Product, then Model, then Colour. Each answer narrows the pool, and a
      // range that has no model or no colourway is never asked about one, so a
      // Robe row shows Product and its own Shopify options and nothing else.

      groupOf(product) {
        return (product && product.group) || 'other';
      }

      groups() {
        const seen = new Map();
        this.catalogue.forEach((p) => {
          const key = this.groupOf(p);
          if (!seen.has(key)) seen.set(key, p.groupLabel || p.title || '');
        });
        return Array.from(seen, ([value, label]) => ({ value, label }));
      }

      inGroup(key) {
        return this.catalogue.filter((p) => this.groupOf(p) === key);
      }

      modelsIn(key) {
        const seen = [];
        this.inGroup(key).forEach((p) => {
          if (p.model && !seen.includes(p.model)) seen.push(p.model);
        });
        return seen;
      }

      // Colours are read from the products that survive the chosen model, so
      // Pro Portable — a Grey-only product of its own — can only ever offer
      // Grey, and no impossible Black Pro Portable is presented.
      coloursIn(key, model) {
        const seen = [];
        this.inGroup(key)
          .filter((p) => !model || p.model === model)
          .forEach((p) => {
            if (p.colour && !seen.includes(p.colour)) seen.push(p.colour);
          });
        return seen;
      }

      // Strict: a product only matches when it genuinely answers both, so a
      // colour carried over from the previous model can never drag the row onto
      // a product of the wrong model. Callers fall back deliberately.
      matchIn(key, model, colour) {
        return (
          this.inGroup(key).find(
            (p) => (!model || p.model === model) && (!colour || p.colour === colour)
          ) || null
        );
      }

      firstIn(key) {
        return this.inGroup(key)[0] || this.catalogue[0];
      }

      // The name a customer would use, assembled from the answers rather than
      // from a supplier title: "Heated Pants — Standard, Black".
      displayName(product) {
        if (!product) return '';
        const base = product.groupLabel || product.title || '';
        const parts = [product.model, product.colour].filter(Boolean);
        return parts.length ? `${base} — ${parts.join(', ')}` : base;
      }

      // Switching product replaces the row's controls, so any option chosen for
      // the previous product is discarded rather than carried into one where it
      // does not exist.
      setProduct(row, id, focusKey) {
        const select = row.querySelector('[data-row-product]');
        if (!select) return;
        const wanted = String(id);
        if (!Array.from(select.options).some((opt) => opt.value === wanted)) return;
        select.value = wanted;
        row.dataset.focusField = focusKey || '';
        this.buildRow(row);
        this.recalculate();
      }

      menu(id, values, current, onChange, extraClass) {
        const select = document.createElement('select');
        select.className = `warmoura-bundle__select${extraClass ? ` ${extraClass}` : ''}`;
        select.id = id;
        values.forEach((value) => {
          const opt = document.createElement('option');
          opt.value = value.value;
          opt.textContent = value.label;
          select.appendChild(opt);
        });
        if (values.some((value) => value.value === current)) select.value = current;
        select.addEventListener('change', () => onChange(select.value));
        return select;
      }

      // Controls are built from the row's answers and from the product's real
      // Shopify options, so a Robe row shows Product, Size and Plug type while
      // a Standard row shows Product, Model, Colour, Function and Plug type.
      // An axis the range does not have is never rendered, and an option with
      // one genuine value is shown as a settled value rather than a menu of
      // one. Every control carries its own visible label.
      buildRow(row) {
        const product = this.productFor(row);
        const host = row.querySelector('[data-row-options]');
        const thumb = row.querySelector('[data-row-thumb]');
        const name = row.querySelector('[data-row-name]');
        if (!product || !host) return;

        const uid = host.dataset.rowUid || '';
        const current = this.currentVariantOptions(product);
        const groupKey = this.groupOf(product);
        const specs = [];

        const settled = (text) => {
          const value = document.createElement('span');
          value.className = 'warmoura-bundle__field-value';
          value.textContent = text || '';
          return value;
        };

        // 1. Product — always full range names, never a colour or a model.
        const groups = this.groups();
        const multiGroup = groups.length > 1;
        specs.push({
          key: 'group',
          label: this.dataset.productLabel || 'Product',
          controlId: multiGroup ? `BundleGroup-${uid}` : null,
          control: multiGroup
            ? this.menu(
                `BundleGroup-${uid}`,
                groups,
                groupKey,
                (value) => this.setProduct(row, this.firstIn(value).id, 'group'),
                'warmoura-bundle__select--product'
              )
            : settled(product.groupLabel || product.title),
        });

        // 2. Model, where the range has more than one.
        const models = this.modelsIn(groupKey);
        if (models.length > 1) {
          specs.push({
            key: 'model',
            label: this.dataset.modelLabel || 'Model',
            controlId: `BundleModel-${uid}`,
            control: this.menu(
              `BundleModel-${uid}`,
              models.map((value) => ({ value, label: value })),
              product.model || '',
              (value) => {
                // The colourway is kept where the new model is sold in it, so
                // a Grey Standard becomes a Grey Pro Portable rather than
                // silently resetting to Black.
                const next =
                  this.matchIn(groupKey, value, product.colour) ||
                  this.matchIn(groupKey, value) ||
                  this.firstIn(groupKey);
                this.setProduct(row, next.id, 'model');
              }
            ),
          });
        } else if (product.model) {
          specs.push({
            key: 'model',
            label: this.dataset.modelLabel || 'Model',
            control: settled(product.model),
          });
        }

        // 3. Colour. A model sold in one colourway keeps the menu shape so the
        //    row does not jump, but it is disabled: the single colour is a fact
        //    about the model, not a choice being withheld.
        const colours = this.coloursIn(groupKey, product.model);
        if (colours.length > 1) {
          specs.push({
            key: 'colour',
            label: this.dataset.colourLabel || 'Colour',
            controlId: `BundleColour-${uid}`,
            control: this.menu(
              `BundleColour-${uid}`,
              colours.map((value) => ({ value, label: value })),
              product.colour || '',
              (value) => {
                const next =
                  this.matchIn(groupKey, product.model, value) || this.firstIn(groupKey);
                this.setProduct(row, next.id, 'colour');
              }
            ),
          });
        } else if (colours.length === 1) {
          const locked = this.menu(
            `BundleColour-${uid}`,
            [{ value: colours[0], label: colours[0] }],
            colours[0],
            () => {}
          );
          locked.disabled = true;
          locked.classList.add('warmoura-bundle__select--locked');
          specs.push({
            key: 'colour',
            label: this.dataset.colourLabel || 'Colour',
            controlId: `BundleColour-${uid}`,
            control: locked,
          });
        }

        // 4. Two products of one range that share a model and a colour — two
        //    Robes, say — still need telling apart, so they keep a menu of
        //    their own rather than silently resolving to the first.
        const siblings = this.inGroup(groupKey).filter(
          (p) => p.model === product.model && p.colour === product.colour
        );
        if (siblings.length > 1) {
          specs.push({
            key: 'style',
            label: this.dataset.styleLabel || 'Style',
            controlId: `BundleStyle-${uid}`,
            control: this.menu(
              `BundleStyle-${uid}`,
              siblings.map((p) => ({ value: String(p.id), label: p.title })),
              String(product.id),
              (value) => this.setProduct(row, Number(value), 'style')
            ),
          });
        }

        // 5. The product's genuine Shopify options.
        (product.options || []).forEach((option, index) => {
          const values = option.values || [];
          // Keyed by what the option is, not where it sits, so switching range
          // retires Function and brings in Size while Plug type — the same
          // question in both, whatever position it holds — stays put and does
          // not flicker. The index only settles a duplicate name.
          let key = `opt-${option.label}`;
          if (specs.some((spec) => spec.key === key)) key = `${key}-${index}`;

          if (values.length <= 1) {
            const value = document.createElement('span');
            value.className = 'warmoura-bundle__field-value';
            value.dataset.rowOption = String(index);
            value.dataset.value = values[0] ? values[0].value : '';
            value.textContent = values[0] ? values[0].label : '';
            specs.push({ key, label: option.label, control: value });
            return;
          }

          const id = `BundleOption-${uid}-${index}`;
          const select = document.createElement('select');
          select.className = 'warmoura-bundle__select';
          select.id = id;
          select.dataset.rowOption = String(index);
          values.forEach((value) => {
            const opt = document.createElement('option');
            opt.value = value.value;
            opt.textContent = value.label;
            if (current && current[index] === value.value) opt.selected = true;
            select.appendChild(opt);
          });
          select.addEventListener('change', () => {
            this.resolveVariant(row);
            this.recalculate();
          });
          specs.push({ key, label: option.label, controlId: id, control: select });
        });

        if (product.note) {
          specs.push({ key: 'note', className: 'warmoura-bundle__row-note', text: product.note });
        }

        this.renderFields(row, host, specs);

        if (name) name.textContent = this.displayName(product);
        if (thumb && product.image) {
          thumb.src = product.image;
          thumb.alt = this.displayName(product);
        }
        this.resolveVariant(row);
      }

      // Fields are diffed rather than rewritten, so a control that survives a
      // change keeps its place silently and only the genuinely new ones animate
      // in. A field that no longer applies fades out where it stood instead of
      // vanishing between two frames.
      renderFields(row, host, specs) {
        const first = row.dataset.built !== 'true';
        const existing = new Map();
        Array.from(host.children).forEach((node) => {
          // A field already fading from the previous change is superseded by
          // this one, so it goes at once rather than stacking up behind a
          // customer who changes their mind twice in a second.
          if (node.classList.contains('is-leaving')) {
            node.remove();
            return;
          }
          const key = node.dataset.fieldKey;
          if (key) existing.set(key, node);
        });

        const keep = new Set(specs.map((spec) => spec.key));
        // Live fields are placed in order from the front, so a field still
        // fading out keeps the position it held rather than being shunted to
        // the end of the row while it goes.
        let cursor = host.firstChild;
        specs.forEach((spec) => {
          let wrap = existing.get(spec.key);
          const isNew = !wrap;
          if (isNew) {
            wrap = document.createElement('p');
            wrap.dataset.fieldKey = spec.key;
          }
          wrap.className = spec.className || 'warmoura-bundle__field';
          wrap.replaceChildren();

          if (spec.className) {
            wrap.textContent = spec.text || '';
          } else {
            const label = document.createElement(spec.controlId ? 'label' : 'span');
            label.className = 'warmoura-bundle__field-label';
            if (spec.controlId) label.setAttribute('for', spec.controlId);
            label.textContent = spec.label || '';
            wrap.append(label, spec.control);
          }

          if (wrap === cursor) {
            cursor = cursor.nextSibling;
          } else {
            host.insertBefore(wrap, cursor);
          }
          if (isNew && !first) {
            wrap.classList.add('is-entering');
            wrap.addEventListener('animationend', () => wrap.classList.remove('is-entering'), {
              once: true,
            });
          }
        });

        existing.forEach((node, key) => {
          if (keep.has(key)) return;
          node.dataset.fieldKey = '';
          node.classList.add('is-leaving');
          // A field on its way out must stop answering for the row at once:
          // it is still in the DOM for the length of the fade, and a stale
          // value left readable there would resolve the wrong variant.
          node.querySelectorAll('[data-row-option]').forEach((control) => {
            delete control.dataset.rowOption;
          });
          node.querySelectorAll('select').forEach((select) => {
            select.disabled = true;
          });
          window.setTimeout(() => node.remove(), 260);
        });

        row.dataset.built = 'true';
        const focusKey = row.dataset.focusField;
        if (focusKey) {
          row.dataset.focusField = '';
          const target = host.querySelector(`[data-field-key="${focusKey}"] select:not([disabled])`);
          if (target && !first) target.focus();
        }
      }

      currentVariantOptions(product) {
        const id = Number(this.dataset.currentProductId);
        const variantId = Number(this.dataset.currentVariantId);
        if (!id || id !== product.id) return null;
        const variant = product.variants.find((v) => v.id === variantId);
        return variant ? variant.options : null;
      }

      // Answers are collected by the option index they belong to rather than by
      // the order the controls happen to sit in, so a control that survives a
      // product change and lands in a different position still answers for the
      // option it was built for.
      selectedValues(row) {
        const values = [];
        row.querySelectorAll('[data-row-option]').forEach((control) => {
          const index = Number(control.dataset.rowOption);
          if (!Number.isFinite(index)) return;
          values[index] = control.tagName === 'SELECT' ? control.value : control.dataset.value;
        });
        return values;
      }

      resolveVariant(row) {
        const product = this.productFor(row);
        const store = row.querySelector('[data-row-variant]');
        if (!product || !store) return;

        const chosen = this.selectedValues(row);
        const matches = (variant) => chosen.every((value, i) => !value || variant.options[i] === value);
        let variant = product.variants.find((v) => matches(v) && v.available);
        if (!variant) variant = product.variants.find(matches);

        store.innerHTML = '';
        if (variant && variant.available) {
          const opt = document.createElement('option');
          opt.value = String(variant.id);
          opt.textContent = variant.title;
          opt.dataset.price = String(variant.price);
          // Genuine compare-at only. A variant whose compare-at is missing, or
          // is not above the selling price, carries none — there is no "was"
          // to show for it and one is never invented.
          const compare = Number(variant.compare_at_price);
          if (Number.isFinite(compare) && compare > Number(variant.price)) {
            opt.dataset.compareAt = String(compare);
          }
          store.appendChild(opt);
          store.value = String(variant.id);
        }

        // A value that no variant of this product carries alongside the other
        // answers is an impossible combination, so it is taken out of the menu
        // rather than offered and refused. A value that exists but is sold out
        // stays visible and disabled: that is stock news, not a wrong turn.
        let repaired = false;
        row.querySelectorAll('select[data-row-option]').forEach((select) => {
          const index = Number(select.dataset.rowOption);
          Array.from(select.options).forEach((opt) => {
            const trial = chosen.slice();
            trial[index] = opt.value;
            const fits = (v) => trial.every((value, i) => !value || v.options[i] === value);
            const exists = product.variants.some(fits);
            opt.hidden = !exists;
            opt.disabled = !product.variants.some((v) => v.available && fits(v));
          });

          const selected = select.selectedOptions[0];
          if (selected && selected.hidden) {
            const fallback =
              Array.from(select.options).find((opt) => !opt.hidden && !opt.disabled) ||
              Array.from(select.options).find((opt) => !opt.hidden);
            if (fallback && fallback.value !== select.value) {
              select.value = fallback.value;
              repaired = true;
            }
          }
        });

        // Repairing a selection changes the answers, so the variant is resolved
        // once more against them. The guard keeps that to a single extra pass.
        if (repaired && !this.repairing) {
          this.repairing = true;
          this.resolveVariant(row);
          this.repairing = false;
          return;
        }

        this.markAvailability(row);
      }

      markAvailability(row) {
        const store = row.querySelector('[data-row-variant]');
        const label = row.querySelector('[data-row-availability]');
        const usable = Boolean(store && store.value);
        row.classList.toggle('is-unavailable', !usable);
        if (label) {
          label.textContent = usable
            ? this.dataset.inStockLabel || 'In stock'
            : this.dataset.outLabel || 'Unavailable';
        }
        return usable;
      }

      rowPrice(row) {
        const opt = row.querySelector('[data-row-variant]')?.selectedOptions?.[0];
        return Number(opt?.dataset.price) || 0;
      }

      // What this row was, where Shopify genuinely records it. Falling back to
      // the selling price is what keeps a mixed bundle honest: an item with no
      // compare-at contributes what it actually costs, so the crossed-out total
      // is never inflated by a figure nobody ever charged.
      rowCompareAt(row) {
        const opt = row.querySelector('[data-row-variant]')?.selectedOptions?.[0];
        const compare = Number(opt?.dataset.compareAt);
        const price = this.rowPrice(row);
        return Number.isFinite(compare) && compare > price ? compare : price;
      }

      // ---- pricing ---------------------------------------------------------

      // Shopify allocates an automatic percentage discount per line and floors
      // each allocation to whole minor units, so the same is done here rather
      // than discounting the basket total. Rounding the discounted price
      // instead drifts by a penny or two on some baskets.
      totalsFor(entry) {
        let regular = 0;
        let saving = 0;
        let compareAt = 0;
        let usable = entry.rows.length > 0;
        entry.rows.forEach((row) => {
          const price = this.rowPrice(row);
          regular += price;
          compareAt += this.rowCompareAt(row);
          saving += Math.floor((price * entry.percent) / 100);
          if (!this.markAvailability(row)) usable = false;
        });
        const total = regular - saving;
        // The headline saving measures the bundle against what those exact
        // items would otherwise cost, compare-at included where it is real.
        const against = Math.max(compareAt, regular);
        const percentOff = against > total ? Math.round(((against - total) / against) * 100) : 0;
        return { regular, saving, total, compareAt: against, percentOff, percent: entry.percent, usable };
      }

      recalculate() {
        const active = this.selectedTier();

        this.tiers.forEach((entry) => {
          const { total, compareAt, percentOff } = this.totalsFor(entry);
          entry.rows.forEach((row) => {
            const el = row.querySelector('[data-row-price]');
            if (el) el.textContent = money(this.rowPrice(row), this.currency, this.locale);
          });

          const totalEl = entry.option.querySelector('[data-tier-total]');
          const compareEl = entry.option.querySelector('[data-tier-regular]');
          const savingEl = entry.option.querySelector('[data-tier-saving]');
          if (totalEl) totalEl.textContent = money(total, this.currency, this.locale);
          if (compareEl) {
            // A crossed-out figure is only shown where these exact items really
            // do cost more apart: the compare-at where Shopify records one, the
            // undiscounted total otherwise.
            const show = compareAt > total;
            compareEl.hidden = !show;
            compareEl.textContent = show ? money(compareAt, this.currency, this.locale) : '';
          }
          if (savingEl) {
            const show = percentOff > 0;
            savingEl.hidden = !show;
            savingEl.textContent = show ? `${this.dataset.savePrefix || 'Save'} ${percentOff}%` : '';
          }
        });

        const summary = this.totalsFor(active);
        const set = (selector, value) => {
          const el = this.querySelector(selector);
          if (el) el.textContent = value;
        };
        set('[data-bundle-regular]', money(summary.compareAt, this.currency, this.locale));
        set('[data-bundle-total]', money(summary.total, this.currency, this.locale));

        const discountRow = this.querySelector('[data-bundle-discount-row]');
        if (summary.compareAt > summary.total) {
          const savePrefix = this.dataset.savePrefix || 'Save';
          set('[data-bundle-discount-label]', `${savePrefix} ${summary.percentOff}%`);
          set(
            '[data-bundle-discount-amount]',
            `−${money(summary.compareAt - summary.total, this.currency, this.locale)}`
          );
          if (discountRow) discountRow.hidden = false;
        } else if (discountRow) {
          discountRow.hidden = true;
        }

        // The button carries the price the customer will actually pay.
        if (this.submitLabel && !this.classList.contains('is-success')) {
          const base = active.option.dataset.tierButton || 'Add to cart';
          this.submitLabel.textContent = `${base} — ${money(summary.total, this.currency, this.locale)}`;
        }
        if (this.submitButton) {
          this.submitButton.disabled = !summary.usable;
          this.submitButton.setAttribute('aria-disabled', String(!summary.usable));
        }
        if (!summary.usable) {
          this.setError(this.dataset.errorText || 'Please choose an available option for every item.');
        } else if (!this.submitting) {
          this.setError('');
        }
      }

      setError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      // ---- cart ------------------------------------------------------------

      collectItems(entry) {
        const bundleId = `wb-${Date.now().toString(36)}`;
        const cartLabel = entry.option.dataset.tierCartLabel || '';
        const order = [];
        const byVariant = new Map();
        entry.rows.forEach((row, index) => {
          const id = Number(row.querySelector('[data-row-variant]')?.value);
          if (!Number.isFinite(id) || id <= 0) return;
          // Identical variants are one Shopify line with quantity 2. The bundle
          // metadata stays correct because both copies belong to one bundle.
          if (byVariant.has(id)) {
            byVariant.get(id).quantity += 1;
            return;
          }
          const properties = {
            _warmoura_bundle_id: bundleId,
            _warmoura_bundle_item: String(index + 1),
            _warmoura_bundle_size: String(entry.rows.length),
          };
          // The image the row is showing travels with the line, so the cart can
          // show the same one without needing to know how it was chosen.
          const rowImage = this.productFor(row)?.image;
          if (rowImage) properties._warmoura_image = rowImage;
          if (cartLabel) properties._bundle = cartLabel;
          const item = { id, quantity: 1, properties };
          byVariant.set(id, item);
          order.push(item);
        });
        const resolved = [...byVariant.values()].reduce((n, i) => n + i.quantity, 0);
        return { items: order, chosen: entry.rows.length, resolved, bundleId };
      }

      async handleSubmit(event) {
        if (!this.form || event.target !== this.form) return;
        const entry = this.selectedTier();
        if (!entry?.rows.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.submitting) return;

        const { items, chosen, resolved } = this.collectItems(entry);
        if (!items.length || resolved !== chosen) {
          this.setError(this.dataset.errorText || 'Please choose an available option for every item.');
          return;
        }

        const expected = this.totalsFor(entry);
        this.submitting = true;
        this.setError('');
        const submit = this.submitButton || this.form?.querySelector('[type="submit"]');
        submit?.classList.add('loading');
        submit?.setAttribute('aria-disabled', 'true');
        submit?.setAttribute('disabled', 'disabled');
        this.form?.querySelector('.loading__spinner')?.classList.remove('hidden');

        try {
          const body = { items };
          const sections = window.WarmouraCartSections;
          if (sections) {
            body.sections = sections;
            body.sections_url = `${window.location.pathname}${window.location.search}`;
          }
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify(body),
          });
          const data = await res.json();
          if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

          // The displayed saving is only honest if Shopify actually applied it,
          // so the real cart is read back before the drawer claims success.
          if (expected.percent > 0) {
            try {
              const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
              if (!cart.total_discount || cart.total_discount <= 0) {
                this.setError(
                  'Your items were added, but the bundle discount did not apply. Please review your bag before checkout.'
                );
              }
            } catch {
              /* Reading back is a check, not a blocker. */
            }
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: items[0].id,
              cartData: data,
            });
          }

          this.refreshCartSurface(data);
          document.dispatchEvent(new CustomEvent('warmoura:bundle-added', { detail: { items } }));
          this.showSuccess();
        } catch (error) {
          this.setError(error.message || 'Add to cart failed');
        } finally {
          this.submitting = false;
          submit?.classList.remove('loading');
          submit?.removeAttribute('aria-disabled');
          submit?.removeAttribute('disabled');
          this.form?.querySelector('.loading__spinner')?.classList.add('hidden');
          this.recalculate();
        }
      }

      showSuccess() {
        const label = this.submitLabel;
        if (!label) return;
        this.classList.add('is-success');
        label.textContent = this.dataset.successLabel || 'Added to cart';
        window.clearTimeout(this.successTimer);
        this.successTimer = window.setTimeout(() => {
          this.classList.remove('is-success');
          this.recalculate();
        }, 1800);
      }

      async refreshCartSurface(data) {
        const surface = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (!surface) return;

        const lines = Array.isArray(data.items) ? data.items : [];
        const lastKey = data.key || lines[lines.length - 1]?.key;
        if (lastKey) surface.cartItemKey = lastKey;

        let sections = data.sections;
        if (!sections && window.WarmouraCartSections) {
          try {
            const params = new URLSearchParams(window.location.search);
            params.set('sections', window.WarmouraCartSections);
            sections = await fetch(`${window.location.pathname}?${params}`).then((r) => r.json());
          } catch {
            sections = null;
          }
        }
        if (!sections) return;

        if (surface.tagName === 'CART-DRAWER') {
          try {
            surface.renderContents({ ...data, sections });
          } catch (error) {
            console.error('Warmoura bundle: cart drawer refresh failed', error);
          }
          return;
        }

        const parse = (html) => new DOMParser().parseFromString(html || '', 'text/html');
        const productTarget = document.getElementById('cart-notification-product');
        if (productTarget && sections['cart-notification-product']) {
          const doc = parse(sections['cart-notification-product']);
          const added = lines
            .map((line) => doc.querySelector(`[id="cart-notification-product-${line.key}"]`))
            .filter(Boolean);
          if (added.length) productTarget.innerHTML = added.map((node) => node.outerHTML).join('');
        }
        ['cart-notification-button', 'cart-icon-bubble'].forEach((id) => {
          const target = document.getElementById(id);
          const source = parse(sections[id]).querySelector('.shopify-section');
          if (target && source) target.innerHTML = source.innerHTML;
        });
        surface.header?.reveal?.();
        surface.open?.();
      }
    }
  );
})();
